import { useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';

import classNames from 'classnames/bind';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import styles from './ImageExcel.module.scss';
import { Box, Button, LinearProgress, Stack } from '@mui/material';
import { menhSimKD, numberFormatText } from '../../../utils/helper';
import copy from 'copy-to-clipboard';

const cx = classNames.bind(styles);

const getMenhStyle = (menh) => {
  const map = {
    Kim: { background: '#f59e0b' },
    Mộc: { background: '#16a34a' },
    Thủy: { background: '#2563eb' },
    Hỏa: { background: '#dc2626' },
    Thổ: { background: '#92400e' },
  };

  return {
    display: 'inline-block',
    padding: '4px 6px',
    borderRadius: 6,
    color: '#fff',
    fontSize: 12,
    textTransform: 'capitalize',
    ...map[menh],
  };
};

const ImageExcel = (data) => {
  const colorTable = data?.color || '#0000ff';
  const colorTableBorder = data?.color + '57' || '#0000ff57';

  const tableRef = useRef([]);
  const [arrImage, setArrImage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const columnCount = Number(data?.column) || 2;

  // SIZE
  const simSize = data?.simSize || 16;
  const priceSize = data?.priceSize || 14;
  const menhSize = data?.menhSize || 14;

  const simSizeHeader = data?.simSizeHeader || 16;
  const priceSizeHeader = data?.priceSizeHeader || 16;
  const menhSizeHeader = data?.menhSizeHeader || 16;

  // 🎨 STYLE CONFIG
  const bgColor = data?.bgColor || '#ffffff';
  const simColor = data?.simColor || '#000000';
  const priceColor = data?.priceColor || '#ef4444';
  const menhColor = data?.menhColor || '#6366f1';
  const headerTextColor = data?.headerTextColor || '#ffffff';
  const titleColor = data?.titleColor || '#000000';
  const footerColor = data?.footerColor || '#000000';

  const fontBody = data?.fontBody || 'Arial';
  const fontHeader = data?.fontHeader || 'Arial';
  const fontFooter = data?.fontFooter || 'Arial';
  const fontTitle = data?.fontTitle || 'Arial';

  const tdBase = {
    padding: '6px 10px',
    whiteSpace: 'nowrap',
  };

  const handleExportImage = (index) => {
    const timer = setTimeout(() => {
      html2canvas(tableRef.current[index], { scale: 3 })
        .then((canvas) => {
          const dataUrl = canvas.toDataURL('image/png', 1);
          saveAs(dataUrl, 'images.png');
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  };

  useEffect(() => {
    handleOpenImage(data?.soluong);
  }, [data]);

  const handleOpenImage = (soluong) => {
    let arrDataImage = [];

    if (data?.dataSim?.length > 0) {
      const iterations = Math.ceil(data?.dataSim.length / soluong);

      for (let i = 0; i < iterations; i++) {
        const batch = data.dataSim.slice(i * soluong, (i + 1) * soluong);

        const rowCount = Math.ceil(batch.length / columnCount);

        const tableData = Array(columnCount)
          .fill(null)
          .map(() => Array(rowCount).fill(null));

        batch.forEach((item, index) => {
          const row = Math.floor(index / rowCount);
          const col = index % rowCount;
          tableData[row][col] = item;
        });

        arrDataImage.push(tableData);
      }

      setArrImage(arrDataImage);
    }
  };

  const handleDownloadAllPages = async () => {
    if (!data?.dataSim || data.dataSim.length === 0 || !data?.soluong) return;

    setLoading(true);
    const zip = new JSZip();
    const columnCount = Number(data?.column) || 2;
    const totalPages = arrImage.length;

    try {
      // Tạo một container ẩn để chứa các phần tử export
      const exportContainer = document.createElement('div');
      exportContainer.style.position = 'absolute';
      exportContainer.style.left = '-9999px';
      exportContainer.style.top = '0';
      document.body.appendChild(exportContainer);

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const pageData = arrImage[pageIndex];

        // 1. Tạo wrapper cho trang hiện tại (giống hệt cấu trúc JSX bên dưới)
        const wrapper = document.createElement('div');
        Object.assign(wrapper.style, {
          background: bgColor,
          padding: '16px',
          display: 'inline-block',
          fontFamily: fontBody,
        });

        // 2. Thêm Title
        if (data?.title) {
          const titleDiv = document.createElement('div');
          titleDiv.textContent = data.title;
          Object.assign(titleDiv.style, {
            textAlign: 'center',
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '10px',
            color: titleColor,
            fontFamily: fontTitle,
          });
          wrapper.appendChild(titleDiv);
        }

        // 3. Tạo Table Container (Flex)
        const tableFlex = document.createElement('div');
        tableFlex.style.display = 'flex';

        pageData.forEach((columnItems, colIdx) => {
          const table = document.createElement('table');
          table.style.borderCollapse = 'collapse';

          // Header
          const thead = document.createElement('thead');
          const trHead = document.createElement('tr');
          trHead.style.background = colorTable;
          trHead.style.color = headerTextColor;

          const headers = [
            { text: data.titleColsA, size: simSizeHeader, show: true },
            { text: data.titleColsB, size: priceSizeHeader, show: !data?.isPrice },
            { text: data.titleColsC, size: menhSizeHeader, show: data?.isMenh },
          ];

          headers.forEach((h) => {
            if (h.show) {
              const th = document.createElement('th');
              th.textContent = h.text;
              Object.assign(th.style, { padding: '8px', fontSize: `${h.size}px`, fontFamily: fontHeader });
              trHead.appendChild(th);
            }
          });
          thead.appendChild(trHead);
          table.appendChild(thead);

          // Body
          const tbody = document.createElement('tbody');
          columnItems.forEach((cell) => {
            if (!cell) return;
            const menhData = menhSimKD(cell?.sim_show);
            const tr = document.createElement('tr');

            // Cấu trúc render từng cell (Sim, Giá, Mệnh)
            const createTd = (content, specificStyle) => {
              const td = document.createElement('td');
              if (typeof content === 'string') td.textContent = content;
              else td.appendChild(content);

              Object.assign(td.style, {
                ...tdBase,
                borderBottom: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                borderRight: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                borderLeft: data?.isBorder && colIdx === 0 ? `1px solid ${colorTableBorder}` : 'none',
                ...specificStyle,
              });
              return td;
            };

            // TD Sim
            tr.appendChild(
              createTd(cell.sim_show, {
                fontSize: `${simSize}px`,
                color: simColor,
                fontFamily: data?.fontSim || fontBody,
              }),
            );

            // TD Giá
            if (!data?.isPrice) {
              tr.appendChild(
                createTd(numberFormatText(cell.price), {
                  fontSize: `${priceSize}px`,
                  color: priceColor,
                  textAlign: 'center',
                  fontFamily: data?.fontPrice || fontBody,
                }),
              );
            }

            // TD Mệnh
            if (data?.isMenh) {
              const span = document.createElement('span');
              span.textContent = menhData?.menh;
              Object.assign(span.style, {
                ...getMenhStyle(menhData?.menh),
                fontSize: `${menhSize}px`,
              });
              tr.appendChild(createTd(span, { textAlign: 'center' }));
            }

            tbody.appendChild(tr);
          });
          table.appendChild(tbody);
          tableFlex.appendChild(table);
        });
        wrapper.appendChild(tableFlex);

        // 4. Thêm Footer
        if (data?.titleFooter) {
          const footerDiv = document.createElement('div');
          footerDiv.textContent = data.titleFooter;
          Object.assign(footerDiv.style, {
            textAlign: 'center',
            fontSize: '22px',
            fontWeight: '700',
            marginTop: '10px',
            color: footerColor,
            fontFamily: fontFooter,
          });
          wrapper.appendChild(footerDiv);
        }

        exportContainer.appendChild(wrapper);

        // Chụp ảnh bằng toPng
        const dataUrl = await toPng(wrapper, { pixelRatio: 2, skipFonts: false });
        const base64Data = dataUrl.replace(/^data:image\/png;base64,/, '');
        zip.file(`trang_${pageIndex + 1}.png`, base64Data, { base64: true });

        // Xóa để giải phóng bộ nhớ tạm
        exportContainer.removeChild(wrapper);
      }

      // Xuất file ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `danh-sach-sim-${new Date().getTime()}.zip`);

      document.body.removeChild(exportContainer);
    } catch (err) {
      console.error('Lỗi tải tất cả:', err);
      alert('Có lỗi xảy ra khi tạo ảnh. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyImage = async (index) => {
    if (!tableRef.current[index]) return;
    try {
      const dataUrl = await toPng(tableRef.current[index], { pixelRatio: 3 });
      const blob = await (await fetch(dataUrl)).blob();
      const clipboardItem = new window.ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([clipboardItem]);
      alert('Đã copy ảnh trang ' + (index + 1));
    } catch (err) {
      console.error(err);
    }
  };

  const getPrintContent = (pagesData) => {
    const bColor = colorTableBorder || '#ddd';

    const renderRows = (columnItems, colIdx) => {
      const isFirstCol = colIdx === 0;
      return columnItems
        .map((cell) => {
          if (!cell) return '';
          const menhData = menhSimKD(cell?.sim_show);
          const menhStyle = getMenhStyle(menhData?.menh);
          const tdBaseStyle = `padding: 6px 10px; white-space: nowrap; box-sizing: border-box; border-bottom: ${data?.isBorder ? `1px solid ${bColor}` : 'none'}; border-right: ${data?.isBorder ? `1px solid ${bColor}` : 'none'}; border-left: ${data?.isBorder && isFirstCol ? `1px solid ${bColor}` : 'none'};`;

          return `
          <tr>
            <td style="${tdBaseStyle} font-size: ${simSize}px; color: ${simColor}; font-family: ${data?.fontSim || fontBody};">${cell.sim_show}</td>
            ${!data?.isPrice ? `<td style="${tdBaseStyle} text-align: center; font-size: ${priceSize}px; color: ${priceColor}; font-family: ${data?.fontPrice || fontBody};">${numberFormatText(cell.price)}</td>` : ''}
            ${data?.isMenh ? `<td style="${tdBaseStyle} text-align: center; font-family: ${data?.fontMenh || fontBody};"><span style="display: inline-block; padding: 4px 6px; border-radius: 6px; background: ${menhStyle.background}; color: #fff; font-size: ${menhSize}px; text-transform: capitalize; line-height: 1;">${menhData?.menh}</span></td>` : ''}
          </tr>`;
        })
        .join('');
    };

    return pagesData
      .map(
        (page) => `
    <div style="background: ${bgColor}; padding: 15px; display: inline-block; width: 100%; box-sizing: border-box; font-family: ${fontBody};">
      ${data?.title ? `<div style="text-align: center; font-size: 28px; font-weight: 800; margin-bottom: 10px; color: ${titleColor}; font-family: ${fontTitle};">${data.title}</div>` : ''}
      <div style="display: flex; justify-content: center; align-items: flex-start; gap: 0;">
        ${page
          .map(
            (column, cIdx) => `
          <table style="border-collapse: collapse; border-spacing: 0; margin: 0; border-top: ${data?.isBorder ? `1px solid ${bColor}` : 'none'};">
            <thead>
              <tr style="background: ${colorTable}; color: ${headerTextColor};">
                <th style="padding: 8px; font-size: ${simSizeHeader}px; font-family: ${fontHeader}; border-bottom: ${data?.isBorder ? `1px solid ${bColor}` : 'none'}; border-right: ${data?.isBorder ? `1px solid ${bColor}` : 'none'}; border-left: ${data?.isBorder && cIdx === 0 ? `1px solid ${bColor}` : 'none'};">${data.titleColsA}</th>
                ${!data?.isPrice ? `<th style="padding: 8px; font-size: ${priceSizeHeader}px; font-family: ${fontHeader}; border-bottom: ${data?.isBorder ? `1px solid ${bColor}` : 'none'}; border-right: ${data?.isBorder ? `1px solid ${bColor}` : 'none'};">${data.titleColsB}</th>` : ''}
                ${data?.isMenh ? `<th style="padding: 8px; font-size: ${menhSizeHeader}px; font-family: ${fontHeader}; border-bottom: ${data?.isBorder ? `1px solid ${bColor}` : 'none'}; border-right: ${data?.isBorder ? `1px solid ${bColor}` : 'none'};">${data.titleColsC}</th>` : ''}
              </tr>
            </thead>
            <tbody style="background: ${data?.bgrBody};">${renderRows(column, cIdx)}</tbody>
          </table>
        `,
          )
          .join('')}
      </div>
      ${data?.titleFooter ? `<div style="text-align: center; font-size: 22px; font-weight: 700; margin-top: 10px; color: ${footerColor}; font-family: ${fontFooter};">${data.titleFooter}</div>` : ''}
    </div>
  `,
      )
      .join('');
  };
  // --- HÀM BỔ TRỢ 1: Chụp ảnh Element giữ nguyên Font chữ ---
  const captureElementAsImage = async (element) => {
    if (!element) return null;
    try {
      return await toPng(element, {
        pixelRatio: 2, // Độ nét 2x để in A4 không bị vỡ
        skipFonts: false, // Quan trọng: Giữ font chữ
        cacheBust: true,
      });
    } catch (error) {
      console.error('Lỗi chụp ảnh:', error);
      return null;
    }
  };

  // --- HÀM BỔ TRỢ 2: Đẩy ảnh vào Iframe và ra lệnh In ---
  const handlePrintMaster = (imageSources) => {
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: 'none',
    });
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.write(`
    <html>
      <head>
        <title>In danh sách SIM</title>
        <style>
          @page { size: A4; margin: 0; }
          body { margin: 0; padding: 0; background: #fff; }
          img { width: 100%; height: auto; display: block; page-break-after: always; }
          img:last-child { page-break-after: auto; }
        </style>
      </head>
      <body >
        ${imageSources.map((src) => `<img src="${src}" />`).join('')}
      </body>
    </html>
  `);
    doc.close();

    iframe.contentWindow.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  // --- HÀM 1: In trang hiện tại ---
  const handlePrintImage = async (index) => {
    const ref = tableRef.current[index];
    if (!ref) return;

    setLoading(true);
    try {
      // Chụp ảnh trang hiện tại với pixelRatio: 3 để nét border và font
      const img = await toPng(ref, {
        pixelRatio: 3,
        skipFonts: false,
        cacheBust: true,
      });

      if (img) {
        const iframe = document.createElement('iframe');
        Object.assign(iframe.style, {
          position: 'fixed',
          right: '0',
          bottom: '0',
          width: '0',
          height: '0',
          border: 'none',
        });
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.write(`
        <html>
          <head>
            <style>
              /* margin: 0 để xóa sạch URL, tiêu đề, ngày tháng ở các góc */
              @page { 
                size: A4 portrait; 
                margin: 0; 
              }
              body { 
                margin: 0; 
                padding: 0; 
                display: flex;
                justify-content: center;
                align-items: flex-start;
                background: #fff;
              }
              img { 
                width: 100vw; 
                height: 100vh; 
                object-fit: contain; /* Ép bảng luôn nằm trọn trong 1 trang giấy */
                display: block;
                image-rendering: -webkit-optimize-contrast;
              }
            </style>
          </head>
          <body>
            <img src="${img}" />
          </body>
        </html>
      `);
        doc.close();

        iframe.contentWindow.onload = () => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          // Xóa iframe sau khi hộp thoại in đóng lại
          setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
          }, 1000);
        };
      }
    } catch (err) {
      console.error('Lỗi in trang lẻ:', err);
      alert('Không thể tạo bản in cho trang này.');
    } finally {
      setLoading(false);
    }
  };

  // --- HÀM 2: In tất cả các trang (Chuẩn nhất) ---
  const handlePrintAllImages = async () => {
    if (arrImage.length === 0) return;
    setLoading(true);

    const hiddenContainer = document.createElement('div');
    hiddenContainer.style.cssText = 'position:fixed; left:-9999px; top:0; width: max-content; z-index: -1;';
    document.body.appendChild(hiddenContainer);

    const allPageImages = [];

    try {
      for (let i = 0; i < arrImage.length; i++) {
        const tempDiv = document.createElement('div');
        tempDiv.style.cssText = `background:${bgColor}; display:inline-block;`;
        tempDiv.innerHTML = getPrintContent([arrImage[i]]);
        hiddenContainer.appendChild(tempDiv);

        await new Promise((resolve) => setTimeout(resolve, 250));

        const img = await toPng(tempDiv, {
          pixelRatio: 3,
          skipFonts: false,
          cacheBust: true,
        });

        if (img) allPageImages.push(img);
        hiddenContainer.removeChild(tempDiv);
      }

      if (allPageImages.length > 0) {
        const iframe = document.createElement('iframe');
        Object.assign(iframe.style, {
          position: 'fixed',
          right: '0',
          bottom: '0',
          width: '0',
          height: '0',
          border: 'none',
        });
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.write(`
        <html>
          <head>
            <style>
              /* margin: 0 sẽ ẩn sạch URL, Header, Footer của trình duyệt */
              @page { 
                size: A4 portrait; 
                margin: 0; 
              }
              body { 
                margin: 0; 
                padding: 0; 
              }
              img { 
                width: 100vw; 
                height: 100vh; 
                object-fit: contain; /* Ép ảnh luôn nằm gọn trong 1 trang A4 */
                display: block; 
                page-break-after: always; 
                image-rendering: -webkit-optimize-contrast;
              }
              img:last-child { page-break-after: auto; }
            </style>
          </head>
          <body>
            ${allPageImages.map((src) => `<img src="${src}" />`).join('')}
          </body>
        </html>
      `);
        doc.close();

        iframe.contentWindow.onload = () => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
          }, 1000);
        };
      }
    } catch (err) {
      console.error('Lỗi in:', err);
    } finally {
      if (document.body.contains(hiddenContainer)) document.body.removeChild(hiddenContainer);
      setLoading(false);
    }
  };
  return (
    <>
      {/* ACTION */}
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" size="small" onClick={() => setPage((p) => Math.max(p - 1, 0))}>
          Trang trước
        </Button>
        <Button variant="contained" size="small" onClick={() => setPage((p) => Math.min(p + 1, arrImage.length - 1))}>
          Trang sau
        </Button>

        <Button
          color="error"
          variant="contained"
          onClick={() => {
            handleExportImage(page);
            setLoading(true);
          }}
        >
          Tải ảnh {page + 1}
        </Button>

        <Button
          color="success"
          variant="contained"
          onClick={() => {
            handleDownloadAllPages();
            setLoading(true);
          }}
        >
          Tải tất cả
        </Button>

        <Button color="secondary" variant="outlined" onClick={() => handleCopyImage(page)}>
          Copy ảnh {page + 1}
        </Button>

        <Button color="info" variant="outlined" onClick={() => handlePrintImage(page)}>
          In ảnh {page + 1}
        </Button>
        <Button color="info" variant="outlined" onClick={handlePrintAllImages}>
          In tất cả
        </Button>

        <span>
          Trang {page + 1}/{arrImage.length}
        </span>
      </Stack>

      {loading && <LinearProgress />}

      {arrImage[page] && (
        <div>
          <div
            ref={(ref) => (tableRef.current[page] = ref)}
            style={{
              margin: '0 auto',
              background: bgColor,
              padding: 16,
              display: 'inline-block',
            }}
          >
            {/* TITLE */}
            {data?.title && (
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 28,
                  fontWeight: 800,
                  marginBottom: 10,
                  color: titleColor,
                  fontFamily: fontTitle,
                }}
              >
                {data.title}
              </div>
            )}

            {/* TABLE */}
            <div style={{ display: 'flex' }}>
              {arrImage[page].map((item, index) => {
                const isFirst = index === 0;

                return (
                  <table key={index} style={{ borderCollapse: data?.typeTable }}>
                    {/* HEADER */}
                    <thead>
                      <tr style={{ background: colorTable, color: headerTextColor }}>
                        <th
                          style={{
                            padding: 8,
                            fontSize: simSizeHeader,
                            fontFamily: fontHeader,
                            borderRadius: `${data?.radiusTHEAD || 0}px`,
                          }}
                        >
                          {data.titleColsA}
                        </th>

                        {!data?.isPrice && (
                          <th
                            style={{
                              padding: 8,
                              fontSize: priceSizeHeader,
                              fontFamily: fontHeader,
                              borderRadius: `${data?.radiusTHEAD || 0}px`,
                            }}
                          >
                            {data.titleColsB}
                          </th>
                        )}

                        {data?.isMenh && (
                          <th
                            style={{
                              padding: 8,
                              fontSize: menhSizeHeader,
                              fontFamily: fontHeader,
                              borderRight: `1px solid ${colorTable}`,
                              borderLeft: `1px solid ${colorTable}`,
                              borderRadius: `${data?.radiusTHEAD || 0}px`,
                            }}
                          >
                            {data.titleColsC}
                          </th>
                        )}
                      </tr>
                    </thead>

                    {/* BODY */}
                    <tbody style={{ background: data?.bgrBody }}>
                      {item.map((cell, i) => {
                        const menhData = menhSimKD(cell?.sim_show);

                        return (
                          <tr key={i}>
                            <td
                              style={{
                                ...tdBase,
                                fontSize: simSize,
                                color: simColor,
                                fontFamily: fontBody,
                                borderBottom: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                                borderLeft: data?.isBorder && isFirst ? `1px solid ${colorTableBorder}` : 'none',
                                borderRight: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                                fontFamily: data?.fontSim,
                                borderRadius: `${data?.radiusTBody || 0}px`,
                              }}
                            >
                              {cell?.sim_show}
                            </td>

                            {!data?.isPrice && (
                              <td
                                style={{
                                  ...tdBase,
                                  fontSize: priceSize,
                                  color: priceColor,
                                  fontFamily: fontBody,
                                  textAlign: 'center',
                                  borderBottom: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                                  borderRight: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                                  fontFamily: data?.fontPrice,
                                  borderRadius: `${data?.radiusTBody || 0}px`,
                                }}
                              >
                                {numberFormatText(cell?.price)}
                              </td>
                            )}

                            {data?.isMenh && (
                              <td
                                style={{
                                  ...tdBase,
                                  textAlign: 'center',
                                  borderBottom: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                                  borderRight: data?.isBorder ? `1px solid ${colorTableBorder}` : 'none',
                                  fontFamily: data?.fontMenh,
                                  borderRadius: `${data?.radiusTBody || 0}px`,
                                }}
                              >
                                <span
                                  style={{
                                    ...getMenhStyle(menhData?.menh),
                                    fontSize: menhSize,
                                    color: '#fff',
                                  }}
                                >
                                  {menhData?.menh}
                                </span>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })}
            </div>

            {/* FOOTER */}
            {data?.titleFooter && (
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 22,
                  fontWeight: 700,
                  marginTop: 10,
                  color: footerColor,
                  fontFamily: data?.fontFooter,
                }}
              >
                {data.titleFooter}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageExcel;
